import { Box, Button, TextField, Typography, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, Chip, Alert, CircularProgress } from "@mui/material"
import { IAdvisor, FeedbackType } from "../../../../types"
import { useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import ClientProvider from "../../../../modules/ClientProvider"

interface FeedbackFormData {
    title: string;
    content: string;
    type: FeedbackType;
    is_anonymous: boolean;
    priority: number;
    tags: string[];
}

export const FeedbackForm = ({ advisor, onCancel, onSubmit }: { 
    advisor: IAdvisor, 
    onCancel: () => void, 
    onSubmit: (feedback: any) => void 
}) => {
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    
    const [formData, setFormData] = useState<FeedbackFormData>({
        title: "",
        content: "",
        type: FeedbackType.GENERAL,
        is_anonymous: false,
        priority: 3,
        tags: []
    });

    const [newTag, setNewTag] = useState("");

    const feedbackClient = ClientProvider.provideAdvisorFeedbackClient();

    const handleInputChange = (field: keyof FeedbackFormData, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setError(null);
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag("");
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.title.trim() || !formData.content.trim()) {
            setError("Title and content are required");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const accessToken = await getAccessTokenSilently();
            
            // Create the feedback
            const { data: createData, error: createError } = await feedbackClient.createFeedback(
                advisor.survey_link.project.tag,
                {
                    title: formData.title,
                    content: formData.content,
                    type: formData.type,
                    is_anonymous: formData.is_anonymous,
                    priority: formData.priority,
                    tags: formData.tags.length > 0 ? formData.tags : undefined
                },
                accessToken
            );

            if (createError) {
                setError(createError.message || "Failed to create feedback");
                return;
            }

            // Submit the feedback
            const { data: submitData, error: submitError } = await feedbackClient.submitFeedback(
                advisor.survey_link.project.tag,
                createData.feedback.id,
                accessToken
            );

            if (submitError) {
                setError(submitError.message || "Failed to submit feedback");
                return;
            }

            //setSuccess(true);
            //setTimeout(() => {
            //    onSubmit(submitData.feedback);
            //}, 1500);
            onSubmit(submitData.feedback);

        } catch (err) {
            setError(err instanceof Error ? err.message : "An unexpected error occurred");
        } finally {
            setLoading(false);
        }
    };

    //if (success) {
    //    return (
    //        <Box sx={{ textAlign: 'center', py: 4 }}>
    //            <Alert severity="success" sx={{ mb: 2 }}>
    //                Feedback submitted successfully! Thank you for your input.
    //            </Alert>
    //        </Box>
    //    );
    //}

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <Typography variant="body1" mb={3} fontWeight="bold">
                What's one great idea or piece of feedback you have for {advisor.survey_link.project.name}?
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TextField
                label="One line summary"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                fullWidth
                required
                sx={{ mb: 2 }}
                disabled={loading}
            />

            <TextField
                label="Description / Detail"
                value={formData.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                multiline
                rows={4}
                fullWidth
                required
                sx={{ mb: 2 }}
                disabled={loading}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value as FeedbackType)}
                    label="Category"
                    disabled={loading}
                >
                    <MenuItem value={FeedbackType.GENERAL}>General</MenuItem>
                    <MenuItem value={FeedbackType.PRODUCT}>Product</MenuItem>
                    <MenuItem value={FeedbackType.STRATEGY}>Strategy</MenuItem>
                    <MenuItem value={FeedbackType.MARKET}>Market</MenuItem>
                    <MenuItem value={FeedbackType.OPERATIONS}>Operations</MenuItem>
                    <MenuItem value={FeedbackType.FUNDRAISING}>Fundraising</MenuItem>
                </Select>
            </FormControl>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={formData.is_anonymous}
                        onChange={(e) => handleInputChange('is_anonymous', e.target.checked)}
                        disabled={loading}
                    />
                }
                label="Submit anonymously"
                sx={{ mb: 2 }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button 
                    variant="outlined" 
                    onClick={onCancel}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button 
                    variant="contained" 
                    type="submit"
                    disabled={loading || !formData.title.trim() || !formData.content.trim()}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {loading ? 'Submitting...' : 'Submit Feedback'}
                </Button>
            </Box>
        </Box>
    )
}